import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BannerSlide from './BannerSlide';
import { API_BASE_URL } from './config';

// `category` scopes which banners this carousel shows: omitted/null means
// the homepage hero (banners with no category placement set); a category
// name shows only banners an admin placed on that category's page. See
// backend/db/bannersRepo.js's `category` field for the placement concept.
export default function HeroCarousel({ onBannerClick, category = null }) {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [bannersLoaded, setBannersLoaded] = useState(false);
  const [active, setActive]   = useState(0);
  const trackRef   = useRef(null);
  const timerRef   = useRef(null);
  const activeRef  = useRef(0);
  const countRef   = useRef(0);
  const pausedRef  = useRef(false);

  useEffect(() => {
    setBannersLoaded(false);
    fetch(`${API_BASE_URL}/api/banners`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const scoped = data.filter(b => (category ? b.category === category : !b.category));
        setBanners(scoped);
        countRef.current = scoped.length;
      })
      .catch(() => {})
      .finally(() => setBannersLoaded(true));
  }, [category]);

  const slideTo = useCallback((i) => {
    const track = trackRef.current;
    if (!track || !track.children[i]) return;
    track.scrollTo({ left: track.children[i].offsetLeft, behavior: 'smooth' });
    activeRef.current = i;
    setActive(i);
  }, []);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (pausedRef.current || countRef.current <= 1) return;
    timerRef.current = setInterval(() => {
      slideTo((activeRef.current + 1) % countRef.current);
    }, 5000);
  }, [slideTo]);

  useEffect(() => {
    if (!banners.length) return;
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [banners.length, startTimer]);

  const goTo = useCallback((i) => {
    slideTo(i);
    startTimer();
  }, [slideTo, startTimer]);

  const prev = () => goTo((active - 1 + banners.length) % banners.length);
  const next = () => goTo((active + 1) % banners.length);

  const onScroll = () => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const slideW = track.children[0].offsetWidth + 8; // 8 = CSS gap
    const i = Math.min(Math.round(track.scrollLeft / slideW), banners.length - 1);
    if (i !== activeRef.current) {
      activeRef.current = i;
      setActive(i);
    }
  };

  // Until the banners request resolves, hold the hero's space rather than
  // rendering nothing.
  //
  // Returning null here was the single largest layout shift in the app: the
  // hero occupies 207px on a phone and 252px on desktop, and it appeared only
  // once /api/banners resolved -- roughly four seconds in on a cold load --
  // shoving every product row below it down the page. Measured CLS on the
  // homepage was 0.212 mobile and 0.152 desktop, both in Core Web Vitals'
  // "needs improvement" band, and every point of it came from this one node.
  //
  // The placeholder reuses .carousel-slide and .carousel-dots, so the space
  // reserved is whatever those rules compute to (230px desktop, 185px at
  // <=600px) rather than a magic number that would drift the first time the
  // hero is resized. It still avoids the flash of the fallback hero, since it
  // renders no banner content of its own.
  if (!bannersLoaded) {
    return (
      <div className="hero-carousel hero-carousel-placeholder" aria-hidden="true">
        <div className="carousel-track-outer">
          <div className="carousel-track">
            <div className="carousel-slide skeleton" />
          </div>
        </div>
        <div className="carousel-dots" />
      </div>
    );
  }

  // No admin-configured banners? On the homepage, don't collapse the hero
  // slot to nothing -- that leaves a first-time visitor opening on a bare
  // category bar, so show a branded welcome hero instead. A category page
  // with no banner configured for it yet just skips the hero entirely --
  // its own "Category Name" grid header still makes the page feel intentional,
  // and a generic homepage tagline wouldn't fit here.
  if (!banners.length) {
    if (category) return null;
    return (
      <div className="hero-carousel">
        <div className="hero-fallback">
          <div className="hero-fallback-inner">
            <h1 className="hero-fallback-title">{t('home.heroTitle')}</h1>
            <p className="hero-fallback-sub">{t('home.heroSubtitle')}</p>
            <button
              type="button"
              className="hero-fallback-cta"
              onClick={() => document.querySelector('.shop-main')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('home.heroCta')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="hero-carousel"
      onMouseEnter={() => { pausedRef.current = true; clearInterval(timerRef.current); }}
      onMouseLeave={() => { pausedRef.current = false; startTimer(); }}
    >
      {/* Track wrapper holds slides + arrows so arrows center on the track */}
      <div className="carousel-track-outer">
        <div ref={trackRef} className="carousel-track" onScroll={onScroll}>
          {banners.map(banner => (
            <BannerSlide key={banner.id} banner={banner} onClick={() => onBannerClick?.(banner)} />
          ))}
        </div>

        {banners.length > 1 && (
          <>
            <button className="carousel-arrow carousel-arrow-left"  onClick={e => { e.stopPropagation(); prev(); }}>&#8249;</button>
            <button className="carousel-arrow carousel-arrow-right" onClick={e => { e.stopPropagation(); next(); }}>&#8250;</button>
          </>
        )}
      </div>

      {/* Dots sit below the track in normal flow */}
      {banners.length > 1 && (
        <div className="carousel-dots">
          {banners.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot${i === active ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); goTo(i); }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
